import React, { ReactNode, useContext } from 'react';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import AppPage from '../../AppPage';
import Assessments from '../../pages/Assessments/Assessments';
import ForcePackagesGraphPage from '../../pages/ForcePackages/force-package-graph/ForcePackageGraphPage';
import ForcePackages from '../../pages/ForcePackages/ForcePackages';
import Landing from '../../pages/Landing/Landing';
import Login from '../../pages/Login/Login';
import ProjectMenu from '../../pages/ProjectMenu/ProjectMenu';
import ResourcesMenu from '../../pages/ResourcesMenu/ResourcesMenu';
import SimulationPage from '../../pages/Simulations/SimulationPage';
import SymbolsPage from '../../pages/SymbolsPage/SymbolsPage';
import Effectors from '../../pages/SystemsLibrary/effectors/Effectors';
import Emitters from '../../pages/SystemsLibrary/emitters/Emitters';
import Platforms from '../../pages/SystemsLibrary/platforms/Platforms';
import Sensors from '../../pages/SystemsLibrary/sensors/Sensors';
import Systems from '../../pages/SystemsLibrary/systems/Systems';
import SystemsLibraryMenu from '../../pages/SystemsLibrary/SystemsLibraryMenu';
import { NotFound } from '../common/NotFound';
import AppInfoContextProvider, { AppInfoContext } from '../contexts/AppInfoContext';
import ProtectedRoute from './ProtectedRoute';

interface ProtectedAppPageProps {
	children: ReactNode;
}

const ProtectedAppPage: React.FC<ProtectedAppPageProps> = ({ children }) => (
	<AppInfoContextProvider>
		<ProtectedRoute>
			<AppPage>{children}</AppPage>
		</ProtectedRoute>
	</AppInfoContextProvider>
);

const OnPremRoute: React.FC<{ element: ReactNode }> = ({ element }) => {
	const { envVariables } = useContext(AppInfoContext);
	return envVariables.IS_SAAS === false ? <>{element}</> : <NotFound />;
};

const Router: React.FC = () => {
	return (
		<BrowserRouter>
			<Routes>
				<Route
					path="/"
					element={
						<ProtectedAppPage>
							<Landing />
						</ProtectedAppPage>
					}
				/>

				<Route
					path="/login"
					element={
						<AppInfoContextProvider>
							<Login />
						</AppInfoContextProvider>
					}
				/>

				<Route
					path="/:project?/:pid?/systems-library"
					element={
						<ProtectedAppPage>
							<SystemsLibraryMenu />
						</ProtectedAppPage>
					}
				/>

				<Route path="/:project?/:pid?/systems-library">
					<Route
						path="sensors/:systemId?"
						element={
							<ProtectedAppPage>
								<Sensors />
							</ProtectedAppPage>
						}
					/>
					<Route
						path="emitters/:systemId?"
						element={
							<ProtectedAppPage>
								<Emitters />
							</ProtectedAppPage>
						}
					/>
					<Route
						path="effectors/:systemId?"
						element={
							<ProtectedAppPage>
								<Effectors />
							</ProtectedAppPage>
						}
					/>
					<Route
						path="platforms/:systemId?"
						element={
							<ProtectedAppPage>
								<Platforms />
							</ProtectedAppPage>
						}
					/>
					<Route
						path="systems/:systemId?"
						element={
							<ProtectedAppPage>
								<Systems />
							</ProtectedAppPage>
						}
					/>
				</Route>

				<Route
					path="/project/:pid"
					element={
						<ProtectedAppPage>
							<ProjectMenu />
						</ProtectedAppPage>
					}
				/>
				<Route path="/project/:pid">
					<Route
						path="assessments/:assessmentId?/:scenarioId?"
						element={
							<ProtectedAppPage>
								<Assessments />
							</ProtectedAppPage>
						}
					/>
					<Route
						path="force-packages"
						element={
							<ProtectedAppPage>
								<ForcePackages />
							</ProtectedAppPage>
						}
					/>
					<Route
						path="force-packages/:fpId"
						element={
							<ProtectedAppPage>
								<ForcePackagesGraphPage />
							</ProtectedAppPage>
						}
					/>
				</Route>
				<Route
					path="resources"
					element={
						<ProtectedAppPage>
							<ResourcesMenu />
						</ProtectedAppPage>
					}
				/>
				<Route
					path="resources/symbols"
					element={
						<ProtectedAppPage>
							<SymbolsPage />
						</ProtectedAppPage>
					}
				/>
				<Route
					path="force-packages/:fpId"
					element={
						<ProtectedAppPage>
							<ForcePackagesGraphPage />
						</ProtectedAppPage>
					}
				/>

				<Route
					path="simulation"
					element={
						<ProtectedAppPage>
							<SimulationPage />
						</ProtectedAppPage>
					}
				/>

				<Route path="*" element={<NotFound />} />
			</Routes>
		</BrowserRouter>
	);
};

export default Router;
